import { ReactNode } from 'react';
import { DeviceResponseType } from 'lavva.exalushome/build/js/Services/Devices/IDevice';
import { IDeviceChannel } from 'lavva.exalushome/build/js/Services/Devices/IDeviceChannel';
import {
  BlindActionEnum,
  FacadeActionEnum,
  GateActionEnum,
} from 'lavva.exalushome/build/js/Services/Devices/Tasks/Tasks';
import { LockType, NotifyType } from 'lavva.exalushome/build/js/Services/Scenes/SceneTaskTypes';
import { SceneTaskType, SupportedTaskTypes } from 'lavva.exalushome/build/js/Services/Scenes/Scenes';
import { SupportedArgumentTypes } from 'lavva.exalushome/build/js/Services/Scenes/ScenesBuilder';
import { DayOfWeekInternal } from '../data-access/gql-types/graphql';
import { ChannelType } from '../integrations/exalus/enums';

export enum ActionType {
  Undefined = 'undefined',
  Action = 'action',
  Automation = 'automation',
}

export type ActionChecked = {
  deviceGuid: string;
  title: string;
  icon: ReactNode;
  checked: boolean;
};

export enum ConditionType {
  Undefined = 'undefined',
  Devices = 'devices',
  Schedule = 'schedule',
  Location = 'location',
  Scenarios = 'scenarios',
}

export enum TaskVariant {
  Undefined = 'undefined',
  OnOff = 'onOff',
  Dim = 'dim',
  SetMicrowave = 'setMicrowave',
  SetRGBW = 'setRGBW',
  SetBlind = 'setBlind',
  SetFacade = 'setFacade',
  SetGate = 'setGate',
}

export enum TriggerMethodType {
  IfIsIt = 'ifIsIt',
  WhenChangeOn = 'whenChangeOn',
}

export enum ConditionSourceType {
  Undefined = 'undefined',
  Remotes = 'remotes',
  BlindRemotes = 'blindRemotes',
  FacadeRemotes = 'facadeRemotes',
  Bells = 'bells',
  Temperature = 'temperature',
  Humidity = 'humidity',
  WindSpeed = 'windSpeed',
  WindThreshold = 'windThreshold',
  Brightness = 'brightness',
  Pressure = 'pressure',
  FloodSensor = 'floodSensor',
  Energy = 'energy',
  ReedSensor = 'reedSensor',
  MovementSensor = 'movementSensor',
  Gate = 'gate',
  Time = 'time',
  DaysOfWeek = 'daysOfWeek',
  Timer = 'timer',
  Astronomical = 'astronomical',
  ScenarioExecution = 'scenarioExecution',
}

export type DaysOfWeekOption = {
  label: string;
  value: number;
  checked: boolean;
};

export type DaysOfWeekInternalOption = {
  label: string;
  short: string;
  value: DayOfWeekInternal;
  checked: boolean;
};

export enum DaysPeriod {
  Work = 'work',
  Weekend = 'weekend',
}

export type ConditionDeviceSourceOption = {
  title: string;
  icon: ReactNode;
  sourceType: ConditionSourceType;
  deviceResponses: DeviceResponseType[];
};

export type ConditionSourceOption = {
  title: string;
  icon: ReactNode;
  sourceType: ConditionSourceType;
  allowType: SupportedArgumentTypes;
};

export type ConditionTypeOption = {
  title: string;
  content: string;
  icon: ReactNode;
  type: ConditionType;
};

export type ActionTaskVariantOption = {
  title: string;
  icon: ReactNode;
  variant: TaskVariant;
  types: ChannelType[];
};

export type ActionTaskOption = {
  title: string;
  icon: ReactNode;
  type: SceneTaskType;
};

export type OnOffStateTask = {
  isOn: boolean;
};

export type DimStateTask = {
  brightness: number;
};

export type MicroventilationStateTask = {
  position: number;
};

export type RGBWStateTask = {
  R: number;
  G: number;
  B: number;
  W?: number;
  Brightness: number;
};

export type BlindStateTask<T> = {
  position: number | null;
  action: T;
};

export type GateStateTask<T> = {
  position: number | null;
  action: T;
};

export type GateDailyModeStateTask<T> = {
  action: T;
};

export type GatePartialControlModeStateTask<T> = {
  action: T;
};

export type FacadeStateTask = {
  position: number;
  tilt: number;
  action: FacadeActionEnum;
};

export type DeviceTaskParams = {
  channel: IDeviceChannel;
  state: TaskState;
  variant: TaskVariant;
};

export type DelayTaskParams = {
  delay: string;
};

export type LockTaskParams = {
  lockType: LockType;
  resetLockScenesGuids: string[];
  lockStart: string;
  lockEnd?: string;
};

export type NotifyTaskParams = {
  message: string;
  notifyType: NotifyType;
  notificationClients: string[];
};

export type TaskState =
  | OnOffStateTask
  | DimStateTask
  | MicroventilationStateTask
  | RGBWStateTask
  | BlindStateTask<BlindActionEnum>
  | GateStateTask<GateActionEnum>
  | FacadeStateTask;

export type TaskParams = DeviceTaskParams | DelayTaskParams | LockTaskParams | NotifyTaskParams;

export type Task = {
  id: string;
  taskType: SceneTaskType;
  taskParams: TaskParams;
};

export type TimeItem = {
  hour: number;
  height: number;
};

export enum BrightnessMethod {
  Percent = 'percent',
  Number = 'number',
  State = 'state',
}

export enum CreateTaskValidateMethod {
  Ignore = 'IGNORE',
  TryFix = 'TRY_FIX',
}

export const supportedTaskTypeBySceneTaskType = {
  [SceneTaskType.DelayTask]: SupportedTaskTypes.DelayTask,
  [SceneTaskType.DeviceTask]: SupportedTaskTypes.DeviceTask,
  [SceneTaskType.LockExecution]: SupportedTaskTypes.LockExecutionTask,
  [SceneTaskType.NotifyTask]: SupportedTaskTypes.NotifyTaskAppNotify,
};

export type ChannelNotifyAvailable = {
  channel: IDeviceChannel;
  type: DeviceResponseType;
  measurementType?: string;
};

export type MessageByType = {
  key: string;
  unit: string;
  more?: Record<string, { key: string; unit: string }>;
};

export type NewActionForm = {
  name: string;
  icon_name: string;
};
